import { runAuthPolicyHook } from '@xstudios/xapp-auth/services/authPolicyHooks'
import { useFastApiTokenAuthStore } from '@xstudios/xapp-auth/stores/fastApiTokenAuthStore'
import { humanDateShort } from '@xstudios/xapp-core/helpers/datetime'

import {
  attachBearerInterceptor,
  createHttpClient,
} from '../shared/httpClients'

// Intentionally isolated client for FastAPI token auth mode. See guardrail in
// googleJwtAuthService about cross-mode interceptor conflicts.
export const api = createHttpClient('/api')

let isRefreshing = false
const pending = []
let refreshTimer = null

export function cancelTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

export function getTokenExpirationHuman(jwt) {
  const [, payload] = jwt.split('.')
  const { exp } = JSON.parse(atob(payload))
  const expDate = new Date(exp * 1000)
  return humanDateShort(expDate)
}

export function isTokenExpired(jwt) {
  return secondsLeft(jwt) <= 0
}

export function scheduleTokenRefresh(token) {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!token) return

  const timeLeft = secondsLeft(token)
  if (timeLeft <= 0) return

  const refreshBufferSeconds = getRefreshBufferSeconds(timeLeft)
  const refreshIn = Math.max(0, (timeLeft - refreshBufferSeconds) * 1000)
  const refreshAt = new Date(Date.now() + refreshIn)

  console.log(`Token expires ${getTokenExpirationHuman(token)}`)
  console.log(`Token scheduled to refresh ${humanDateShort(refreshAt)}`)

  refreshTimer = setTimeout(async () => {
    try {
      const store = useFastApiTokenAuthStore()
      await store.silentRefresh()
    } catch (error) {
      console.error('Auto-refresh failed:', error)
    }
  }, refreshIn)
}

function flush(token) {
  for (const fn of pending.splice(0)) {
    fn(token)
  }
}

function getRefreshBufferSeconds(timeLeft) {
  if (timeLeft > 300) return 300
  return Math.max(15, Math.floor(timeLeft * 0.2))
}

function queue(fn) {
  pending.push(fn)
}

function secondsLeft(jwt) {
  try {
    const [, payload] = jwt.split('.')
    const { exp } = JSON.parse(atob(payload))
    return exp - Math.floor(Date.now() / 1000)
  } catch {
    return 0
  }
}

attachBearerInterceptor(api, () => useFastApiTokenAuthStore().token)

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.endsWith('/auth/refresh')
    ) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(api(originalRequest))
            } else {
              reject(error)
            }
          })
        })
      }

      isRefreshing = true
      const store = useFastApiTokenAuthStore()

      try {
        const session = await refreshToken()
        store.setSession(session)
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`
        flush(session.access_token)
        return api(originalRequest)
      } catch (refreshError) {
        await runAuthPolicyHook('onRefreshFailed', {
          requestUrl: String(originalRequest?.url || ''),
          status: Number(refreshError?.response?.status || 0),
        })
        flush(null)
        store.clearSession()
        throw refreshError
      } finally {
        isRefreshing = false
      }
    }

    const detail = error?.response?.data?.detail
    const code = String(
      detail?.code || error?.response?.data?.code || ''
    ).trim()
    const message = String(
      detail?.message || detail || error?.response?.data?.message || ''
    ).toLowerCase()
    await runAuthPolicyHook('onRequestError', {
      code,
      detail,
      isAuthTokenEndpoint: false,
      message,
      requestUrl: String(originalRequest?.url || ''),
      status: Number(error?.response?.status || 0),
    })

    throw error
  }
)

export function loginWithPassword(email, password) {
  return api.post('/auth/login', { email, password }).then((r) => r.data)
}

export function logout() {
  return api.post('/auth/logout').then(() => true)
}

export function refreshToken() {
  return api.post('/auth/refresh').then((r) => r.data)
}
